import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from "react-bootstrap-table2-overlay";
import paginationFactory from "react-bootstrap-table2-paginator";
import { Tooltip } from "../../../components/Utils/Tooltips";
import { WithAuth } from "../../../components/Authorization/WithAuth";
import Card from "../../../components/Cards/Card";
import { Add, Edit, Remove } from "../../../components/Icons/Icons";
import { useTranslation } from "react-i18next";
import {
  createDevice,
  editDevice,
  removeDevice,
  getDevices,
} from "../../../api/devices";
import { HandleApiError } from "../../../components/Notifications/APIErrorHandler";

import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import { ModalCreateDevice } from "../../../components/ClinicalTrials/Devices/ModalCreateDevice";
import { toast } from "react-toastify";
import { ModalEditDevice } from "../../../components/ClinicalTrials/Devices/ModalEditDevice";
import { ModalConfirm } from "../../../components/Modals/ModalConfirm";
import { dateFormatter } from "../../../components/Utils/Formatter";
import { useFiles } from "../../../components/CustomControls/Files/Hooks";
import { getAttachmentEndpoint } from "../../../api/clinicalTrial";
import { attachmentEntityType } from "../../../variables/Enums";
import { DocumentViewer } from "../../../components/CustomControls/DocViewer/DocumentViewer";
import { decodeFileName } from "../../../components/Utils/Commons";

export const Devices = () => {
  const [response, downloadBlob] = useFiles(
    getAttachmentEndpoint(attachmentEntityType.Hospital)
  );
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalSize, setTotalSize] = useState(0);
  const [query, setQuery] = useState({
    pagination: {
      page: 1,
      sizePerPage: 10,
      totalSize: 0,
    },
    sort: {
      sortField: "Created",
      sortOrder: "desc",
    },
    filters: {},
  });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editId, setEditId] = useState();
  const [removeId, setRemoveId] = useState(false);

  useEffect(() => {
    let isSubscribed = true;

    const fetchData = () => {
      setIsLoading(true);
      getDevices(query)
        .then((response) => {
          if (isSubscribed) {
            const paginatedResult = response.data;

            setTotalSize(paginatedResult.count);
            setData(paginatedResult.data);
            setIsLoading(false);
          }
        })
        .catch((error) => {
          if (isSubscribed) {
            HandleApiError(error);
            setIsLoading(false);
          }
        });
    };

    fetchData();

    return () => (isSubscribed = false);
  }, [query]);

  const handleTableChange = (
    type,
    { page, sizePerPage, sortField, sortOrder }
  ) => {
    var newQuery = null;
    if (type === "pagination") {
      newQuery = JSON.parse(JSON.stringify(query));

      newQuery.pagination.page = page;
      newQuery.pagination.sizePerPage = sizePerPage;

      setQuery(newQuery);
    }

    if (type === "sort") {
      newQuery = JSON.parse(JSON.stringify(query));

      newQuery.sort.sortField = sortField;
      newQuery.sort.sortOrder = sortOrder;

      setQuery(newQuery);
    }
  };

  const handleModalClose = (refresh) => {
    setShowCreateModal(false);
    setEditId(undefined);
    setRemoveId(undefined);

    if (refresh) {
      getDevices(query)
        .then((response) => {
          const paginatedResult = response.data;

          setTotalSize(paginatedResult.count);
          setData(paginatedResult.data);
          setIsLoading(false);
        })
        .catch((error) => {
          HandleApiError(error);
          setIsLoading(false);
        });
    }
  };

  const handlePreviewFile = async (attachmentId) => {
    await downloadBlob(attachmentId);
  };

  // TODO: Aplicar filtros una vez validado lo funcional
  // const handleFilterChange = (filterName, selectedValue) => {
  //   if (filterName) {
  //     const newQuery = {
  //       ...query,
  //       filters: { ...query.filters },
  //     };
  //     newQuery.pagination.page = 1;
  //     newQuery.filters[filterName] = selectedValue?.id ?? selectedValue;
  //     setQuery(newQuery);
  //   }
  // };

  const columns = [
    {
      dataField: "name",
      text: t("devices.name"),
      headerStyle: { width: "100px" },
    },
    {
      dataField: "attachmentName",
      text: t("devices.attachment.name"),
      headerStyle: { width: "200px" },
      formatter: (_, row) => {
        if (!row.attachmentName) {
          return "";
        }
        return (
          <a
            href="javascript:void(0)"
            onClick={() => handlePreviewFile(row.attachmentId)}
          >
            {decodeFileName(row.attachmentName)}
          </a>
        );
      },
    },
    {
      dataField: "serialNumber",
      text: t("devices.serialNumber"),
      headerStyle: { width: "100px" },
    },
    {
      dataField: "attachmentDueDate",
      text: t("devices.attachment.dueDate"),
      // sort: true,
      headerStyle: { width: "200px" },
      formatter: (_, row) => {
        if (!row.attachmentDueDate) {
          return "";
        }
        return dateFormatter(row.attachmentDueDate, "DD/MM/YY");
      },
    },
    {
      dataField: "description",
      text: t("devices.description"),
      headerStyle: { width: "100px" },
    },
    {
      dataField: "view",
      text: "",
      isDummyField: true,
      headerClasses: "header-protocol-actions",
      classes: "row-protocol-actions",
      headerStyle: { width: "100px" },
      headerAlign: "center",
      align: "center",
      formatter: (_cell, row, rowIndex) => (
        <div key={rowIndex}>
          <WithAuth requiredPermission={["Devices.Edit"]}>
            <button
              className="btn-table-actions"
              onClick={() => setEditId(row.id)}
            >
              <Tooltip
                tooltip={t("devices.list.editTooltip")}
                id={"tooltipedit-" + row.id}
              >
                <span aria-hidden="true">
                  <Edit />
                </span>
              </Tooltip>
            </button>
          </WithAuth>
          <WithAuth requiredPermission={["Devices.Delete"]}>
            <button
              className="btn-table-actions"
              onClick={() => setRemoveId(row.id)}
            >
              <Tooltip
                tooltip={t("devices.list.removeTooltip")}
                id={"tooltipedit-" + row.id}
              >
                <span aria-hidden="true">
                  <Remove />
                </span>
              </Tooltip>
            </button>
          </WithAuth>
        </div>
      ),
    },
  ];

  return (
    <>
      <Card
        header={
          <Row>
            <Col md={2}>
              <WithAuth requiredPermission={["Devices.Create"]}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setShowCreateModal(true);
                  }}
                >
                  <Add />
                  {t("devices.list.addDevice")}
                </button>
              </WithAuth>
            </Col>
            {/* TODO: Crear filtros al confirmar funcionalidad 
              <ClinicalTrialsFilters
              isLoading={isLoadingMasterData}
              masterData={masterData}
              onChangeFilter={handleFilterChange}
            /> */}
          </Row>
        }
        content={
          <BootstrapTable
            bordered={false}
            hover
            remote
            keyField="id"
            data={data}
            columns={columns}
            classes="table-spaced table-spaced-small"
            wrapperClasses="table-responsive"
            pagination={paginationFactory({
              page: query.pagination.page,
              sizePerPage: query.pagination.sizePerPage,
              totalSize: totalSize,
              hideSizePerPage: true,
            })}
            onTableChange={handleTableChange}
            noDataIndication={() => <>{t("commons.noData")}</>}
            loading={isLoading}
            overlay={overlayFactory({ spinner: true })}
          />
        }
      />
      {showCreateModal && (
        <ModalCreateDevice
          show={showCreateModal}
          onClose={handleModalClose}
          onSave={async (payload, setSaving) => {
            try {
              const result = await createDevice(payload);

              if (result.status === 200) {
                handleModalClose(true);
                return;
              }
              toast.error(t("errors.serverError"));
            } catch (error) {
              HandleApiError(error);
            } finally {
              setSaving(false);
            }
          }}
        />
      )}

      {editId && (
        <ModalEditDevice
          deviceId={editId}
          show={!!editId}
          onClose={() => {
            handleModalClose(true);
          }}
          onSave={async (payload, setSaving) => {
            try {
              const result = await editDevice(editId, payload);

              if (result.status === 200) {
                handleModalClose(true);
                return;
              }
              toast.error(t("errors.serverError"));
            } catch (error) {
              HandleApiError(error);
            } finally {
              setSaving(false);
            }
          }}
        />
      )}
      {removeId && (
        <ModalConfirm
          onConfirm={async () => {
            try {
              const removeResponse = await removeDevice(removeId);
              if (removeResponse.status == 200) {
                handleModalClose(true);
              } else {
                toast.error(t("errors.serverError"));
              }
            } catch (error) {
              HandleApiError(error);
            } finally {
              setRemoveId(undefined);
            }
          }}
          onCancel={() => setRemoveId(undefined)}
          title={t("devices.list.removeConfirmationTitle")}
          description={t("devices.list.removeConfirmationMessage")}
          confirmStyle="danger"
        />
      )}
      <DocumentViewer
        file={response?.blob}
        fileName={response?.fileName}
        inModal={true}
        isLoading={false}
      />
    </>
  );
};

export default Devices;
